import { Component, Input, OnInit } from '@angular/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzFormItemComponent } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NzMessageService } from 'ng-zorro-antd/message';

interface GuidelineItem {
  sequence: number;
  guideline: string;
}

@Component({
  selector: 'app-guidelines',
  standalone: true,
  imports: [
    NzFormItemComponent,
    NzTableModule,
    NzButtonModule,
    SvgIconComponent,
    NzInputModule,
    CdkDropList,
    CdkDrag,
    DragDropModule,
    CommonModule,
  ],
  templateUrl: './guidelines.component.html',
  styleUrl: './guidelines.component.scss',
})
export class GuidelinesComponent implements OnInit {
  @Input() guidelineForm!: FormGroup;
  listOfData: GuidelineItem[] = [];

  readonly maxLength = 500;
  readonly minLength = 3;

  constructor(
    private fb: FormBuilder,
    private message: NzMessageService
  ) {}

  ngOnInit() {
    this.initializeFormArray();
  }

  private initializeFormArray() {
    if (!this.guidelineForm.get('items')) {
      this.guidelineForm.addControl('items', this.fb.array([]));
    }

    if (this.getItemsFormArray().length === 0) {
      this.listOfData = [];
      this.listOfData.push({
        sequence: 1,
        guideline: '',
      });
      this.updateFormFromList();
    } else {
      this.updateListFromForm();
    }
  }

  private getItemsFormArray(): FormArray {
    return this.guidelineForm.get('items') as FormArray;
  }

  private updateListFromForm() {
    const itemsArray = this.getItemsFormArray();
    this.listOfData = itemsArray.controls.map((control, index) => ({
      sequence: index + 1,
      guideline: (control as FormGroup).get('guideline')?.value || '',
    }));
  }

  private updateFormFromList() {
    const itemsArray = this.getItemsFormArray();
    itemsArray.clear();

    this.listOfData.forEach((item, index) => {
      itemsArray.push(
        this.fb.group({
          sequence: [index + 1],
          guideline: [
            item.guideline,
            [
              Validators.required,
              Validators.minLength(this.minLength),
              Validators.maxLength(this.maxLength),
            ],
          ],
        })
      );
    });
  }

  addRow() {
    const lastItemIndex = this.listOfData.length - 1;
    if (
      lastItemIndex >= 0 &&
      !this.listOfData[lastItemIndex].guideline.trim()
    ) {
      this.message.warning(
        'Please fill in the current guideline before adding a new one'
      );
      return;
    }

    this.listOfData.push({
      sequence: this.listOfData.length + 1,
      guideline: '',
    });
    this.updateFormFromList();

    const index = this.listOfData.length - 1;
    const control = this.getItemsFormArray().at(index)?.get('guideline');
    control?.markAsTouched();
  }

  canAddNewRow(): boolean {
    const lastItemIndex = this.listOfData.length - 1;
    return (
      lastItemIndex < 0 || !!this.listOfData[lastItemIndex].guideline.trim()
    );
  }

  removeRow(index: number) {
    this.listOfData.splice(index, 1);
    this.listOfData.forEach((item, idx) => {
      item.sequence = idx + 1;
    });
    this.updateFormFromList();
  }

  updateGuideline(index: number, event: Event) {
    const target = event.target as HTMLTextAreaElement;
    this.listOfData[index].guideline = target.value;
    this.updateFormFromList();

    const control = this.getItemsFormArray().at(index)?.get('guideline');
    control?.markAsTouched();
  }

  drop(event: CdkDragDrop<GuidelineItem[]>) {
    moveItemInArray(this.listOfData, event.previousIndex, event.currentIndex);
    this.listOfData.forEach((item, index) => {
      item.sequence = index + 1;
    });
    this.updateFormFromList();
  }

  isInvalid(index: number): boolean {
    const control = this.getItemsFormArray().at(index)?.get('guideline');
    return !!control?.invalid && (!!control?.dirty || !!control?.touched);
  }

  getError(index: number): string {
    const control = this.getItemsFormArray().at(index)?.get('guideline');
    if (!control?.errors || (!control.dirty && !control.touched)) return '';

    const messages = {
      required: 'Guideline is required.',
      minlength: `Minimum ${this.minLength} characters required.`,
      maxlength: `Maximum ${this.maxLength} characters allowed.`,
    };

    const errorType = Object.keys(control.errors)[0] as keyof typeof messages;
    return messages[errorType] || 'Invalid input.';
  }
}
